/**
 * UTM capture: on first visit (or any visit with utm_* in URL), persist UTM/referrer/landing
 * data in sessionStorage so it travels with the user across pages until they submit a form.
 *
 * - Captures `utm_source`, `utm_medium`, `utm_campaign`, `utm_term`, `utm_content`, `gclid`, `fbclid`.
 * - Captures `document.referrer` and the landing path on first arrival.
 * - getUtm() returns an object suitable to spread into a form payload.
 */
const KEY = "esh_utm_payload";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
];

function _safeSession() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function captureUtmFromUrl() {
  const storage = _safeSession();
  if (!storage) return;

  let url;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  const params = url.searchParams;

  // Only refresh storage if any UTM is present, OR there's nothing stored yet
  const incoming = {};
  let hasUtm = false;
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) {
      incoming[k] = v.slice(0, 200);
      hasUtm = true;
    }
  }

  const existing = (() => {
    try {
      return JSON.parse(storage.getItem(KEY) || "null");
    } catch {
      return null;
    }
  })();

  if (!existing && !hasUtm) {
    // First visit, no UTM in URL — still capture referrer + landing path
    const seed = {
      referrer: (document.referrer || "").slice(0, 400),
      landing_path: (url.pathname + url.search).slice(0, 400),
      captured_at: new Date().toISOString(),
    };
    storage.setItem(KEY, JSON.stringify(seed));
    return;
  }

  if (hasUtm) {
    const merged = {
      ...(existing || {}),
      ...incoming,
      referrer: (existing && existing.referrer) || (document.referrer || "").slice(0, 400),
      landing_path:
        (existing && existing.landing_path) ||
        (url.pathname + url.search).slice(0, 400),
      captured_at: new Date().toISOString(),
    };
    storage.setItem(KEY, JSON.stringify(merged));
  }
}

export function getUtm() {
  const storage = _safeSession();
  if (!storage) return undefined;
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed && Object.keys(parsed).length ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function clearUtm() {
  const storage = _safeSession();
  if (!storage) return;
  storage.removeItem(KEY);
}
